/**
 * Painel de administração de usuários — só para admin (is_staff).
 *
 * Traz para dentro do SPA o que antes só existia no Django admin: criar, listar,
 * ativar/desativar, liberar envio de planilhas e redefinir senha. Promover a
 * admin (is_staff) fica de fora de propósito — segue só no Django admin, para
 * este painel não virar um caminho de escalada de privilégio.
 *
 * O gating de rota/menu é cortesia; quem recusa de verdade é o backend, que
 * responde 403 a quem não é staff.
 */

import { useState } from 'react'

import { Botao } from '../../components/Botao'
import { Cartao } from '../../components/Cartao'
import { Entrada, Interruptor } from '../../components/Campo'
import { Aviso, Carregando, Erro } from '../../components/Estado'
import { Pagina, Pilha } from '../../components/Pagina'
import { Tabela, type Coluna } from '../../components/Tabela'
import { ErroApi } from '../../lib/api'
import { contagem, dataHora } from '../../lib/formato'
import type { UsuarioAdmin } from '../../lib/tipos'
import { useSessao } from '../../stores/sessao'
import { useAlternarAtivo, useSalvarUsuario, useUsuarios } from './hooks'
import estilos from './Usuarios.module.css'

export function UsuariosPage() {
  const usuarioAtual = useSessao((estado) => estado.usuario)

  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)
  const [emEdicao, setEmEdicao] = useState<UsuarioAdmin | null>(null)

  const consulta = useUsuarios(busca, pagina)
  const alternar = useAlternarAtivo()

  const colunas: Coluna<UsuarioAdmin>[] = [
    {
      chave: 'username',
      cabecalho: 'Usuário',
      celula: (linha) => (
        <span>
          {linha.username}
          {linha.nome !== linha.username && (
            <span className={estilos.usuarioNome}> · {linha.nome}</span>
          )}
        </span>
      ),
    },
    { chave: 'email', cabecalho: 'E-mail', celula: (linha) => linha.email || '—' },
    {
      chave: 'podeIngerir',
      cabecalho: 'Envia planilhas',
      largura: '130px',
      celula: (linha) => (
        <span className={`${estilos.selo} ${linha.podeIngerir ? estilos.seloBom : estilos.seloApagado}`}>
          {linha.podeIngerir ? 'Sim' : 'Não'}
        </span>
      ),
    },
    {
      chave: 'isActive',
      cabecalho: 'Situação',
      largura: '110px',
      celula: (linha) => (
        <span className={`${estilos.selo} ${linha.isActive ? estilos.seloBom : estilos.seloApagado}`}>
          {linha.isActive ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      chave: 'criado',
      cabecalho: 'Criado',
      largura: '140px',
      celula: (linha) => <span className={estilos.usuarioNome}>{dataHora(linha.dateJoined)}</span>,
    },
    {
      chave: 'acoes',
      cabecalho: '',
      largura: '180px',
      celula: (linha) => {
        const ehEuMesmo = linha.id === usuarioAtual?.id
        return (
          <div className={estilos.acoesLinha}>
            <Botao tamanho="pequeno" variante="sutil" onClick={() => setEmEdicao(linha)}>
              Editar
            </Botao>
            <Botao
              tamanho="pequeno"
              variante={linha.isActive ? 'perigo' : 'secundario'}
              // Não dá para se desativar sozinho e se trancar para fora — o
              // backend também recusa, isto só evita a viagem.
              disabled={(linha.isActive && ehEuMesmo) || alternar.isPending}
              onClick={() => alternar.mutate({ id: linha.id, isActive: !linha.isActive })}
            >
              {linha.isActive ? 'Desativar' : 'Ativar'}
            </Botao>
          </div>
        )
      },
    },
  ]

  const total = consulta.data?.count ?? 0
  const paginas = Math.max(1, Math.ceil(total / 50))

  return (
    <Pagina
      titulo="Usuários"
      descricao="Criação e gestão de acesso ao painel. Só administradores veem esta tela. Desativar preserva o histórico; promover a administrador continua no Django admin."
    >
      <Pilha>
        <Formulario
          key={emEdicao?.id ?? 'novo'}
          registro={emEdicao}
          aoFechar={() => setEmEdicao(null)}
        />

        {alternar.isError && <Erro erro={alternar.error} />}

        <Cartao
          titulo={`${contagem(total)} usuário(s)`}
          acoes={
            <div className={estilos.buscaLinha}>
              <input
                className={estilos.busca}
                placeholder="Buscar por usuário, e-mail ou nome…"
                value={busca}
                onChange={(evento) => {
                  setBusca(evento.target.value)
                  setPagina(1)
                }}
              />
              {paginas > 1 && (
                <div className={estilos.paginacao}>
                  <Botao tamanho="pequeno" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                    Anterior
                  </Botao>
                  <span className={estilos.paginaContador}>
                    {pagina} de {paginas}
                  </span>
                  <Botao
                    tamanho="pequeno"
                    disabled={pagina >= paginas}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    Próxima
                  </Botao>
                </div>
              )}
            </div>
          }
        >
          {consulta.isPending && !consulta.data && <Carregando />}
          {consulta.isError && <Erro erro={consulta.error} aoTentarDeNovo={() => consulta.refetch()} />}
          {consulta.data && (
            <Tabela
              colunas={colunas}
              linhas={consulta.data.results}
              chaveDe={(linha) => linha.id}
              rotulo="Usuários"
              vazio={busca ? 'Nenhum usuário com esse texto.' : 'Nenhum usuário cadastrado.'}
            />
          )}
        </Cartao>
      </Pilha>
    </Pagina>
  )
}

function Formulario({
  registro,
  aoFechar,
}: {
  registro: UsuarioAdmin | null
  aoFechar: () => void
}) {
  const [username, setUsername] = useState(registro?.username ?? '')
  const [email, setEmail] = useState(registro?.email ?? '')
  const [firstName, setFirstName] = useState(registro?.firstName ?? '')
  const [lastName, setLastName] = useState(registro?.lastName ?? '')
  const [senha, setSenha] = useState('')
  const [podeIngerir, setPodeIngerir] = useState(registro?.podeIngerir ?? false)
  const [isActive, setIsActive] = useState(registro?.isActive ?? true)

  const salvar = useSalvarUsuario()

  function submeter() {
    const dados: Record<string, unknown> = { username, email, firstName, lastName, podeIngerir }
    if (registro) dados.isActive = isActive
    if (senha.trim() !== '') dados.password = senha

    salvar.mutate(
      { id: registro?.id, dados },
      {
        onSuccess: () => {
          if (registro) aoFechar()
          else {
            setUsername('')
            setEmail('')
            setFirstName('')
            setLastName('')
            setSenha('')
            setPodeIngerir(false)
          }
        },
      },
    )
  }

  // Na criação a senha é obrigatória; na edição, em branco mantém a atual.
  const pronto = username.trim() !== '' && (registro !== null || senha.trim() !== '')

  return (
    <Cartao
      titulo={registro ? `Editar "${registro.username}"` : 'Novo usuário'}
      subtitulo="Administrador (is_staff) só se define pelo Django admin — aqui não."
      acoes={
        registro ? (
          <Botao variante="sutil" onClick={aoFechar}>
            Cancelar edição
          </Botao>
        ) : undefined
      }
    >
      <div className={estilos.formulario}>
        <Entrada
          rotulo="Usuário"
          value={username}
          onChange={(evento) => setUsername(evento.target.value)}
          placeholder="joao.silva"
          autoComplete="off"
        />
        <Entrada
          rotulo="E-mail"
          type="email"
          value={email}
          onChange={(evento) => setEmail(evento.target.value)}
          placeholder="joao@example.com"
          autoComplete="off"
        />
        <Entrada
          rotulo="Nome"
          value={firstName}
          onChange={(evento) => setFirstName(evento.target.value)}
          placeholder="João"
        />
        <Entrada
          rotulo="Sobrenome"
          value={lastName}
          onChange={(evento) => setLastName(evento.target.value)}
          placeholder="Silva"
        />
        <Entrada
          rotulo={registro ? 'Nova senha' : 'Senha'}
          type="password"
          value={senha}
          onChange={(evento) => setSenha(evento.target.value)}
          autoComplete="new-password"
          dica={registro ? 'Deixe em branco para manter a atual.' : erroSenha(salvar.error)}
        />
        <Interruptor
          rotulo="Pode enviar planilhas"
          checked={podeIngerir}
          aoMudar={setPodeIngerir}
          descricao="Libera upload e edição dos de-paras."
        />
        {registro && (
          <Interruptor
            rotulo="Ativo"
            checked={isActive}
            aoMudar={setIsActive}
            descricao="Usuário inativo não consegue entrar."
          />
        )}
        <div className={estilos.formularioAcao}>
          <Botao variante="primario" onClick={submeter} disabled={!pronto} carregando={salvar.isPending}>
            {registro ? 'Salvar' : 'Criar usuário'}
          </Botao>
        </div>
      </div>

      {salvar.isError && <Erro erro={salvar.error} />}
      {salvar.isSuccess && !registro && <Aviso tom="bom">Usuário criado. Ele já pode entrar com a senha definida.</Aviso>}
    </Cartao>
  )
}

/** Mensagem de validação de senha vinda do backend, para mostrar junto ao campo. */
function erroSenha(erro: unknown): string | undefined {
  if (!(erro instanceof ErroApi) || typeof erro.corpo !== 'object' || erro.corpo === null) return undefined
  const password = (erro.corpo as Record<string, unknown>).password
  if (Array.isArray(password)) return password.join(' ')
  return typeof password === 'string' ? password : undefined
}
